import * as React from 'react'

import { Box, Checkbox, Text } from '@factorialco/deprecated-design-system'
import { F0DatePicker } from '@factorialco/f0-react'
import { DatePickerValue, Input } from '@factorialco/f0-react/dist/experimental'
import moment from 'moment'
import momentTimezone from 'moment-timezone'

import SelectField from 'modules/core/components/SelectField'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { ScheduleMessageModal } from 'modules/ats/components/ScheduleMessageModal'
import { isCustomDateTimeValue } from 'modules/ats/lib/utils/candidateEmailActions'
import { CUSTOM_DELAY_VALUE, useMessageOptions } from 'modules/ats/lib/utils/messages'

interface Props {
  label: string
  value: string
  onChange: (when: string) => void
  onCustomSelect?: () => void
  inlineCustomPicker?: boolean
}

const formatCustomDateTime = (dateValue: string): string => {
  const dateMoment = stringToDate(dateValue, 'backend_datetime')
  const now = moment()
  const time = dateMoment.format('LT')

  if (dateMoment.isSame(now, 'day')) {
    return i18n.t('ats.candidates.messages.delayed_messages.today_at_time', { time })
  }

  if (dateMoment.isSame(now.clone().add(1, 'day'), 'day')) {
    return i18n.t('ats.candidates.messages.delayed_messages.tomorrow_at_time', { time })
  }

  if (dateMoment.isSame(now, 'week')) {
    const dayName = dateMoment.format('dddd')
    return i18n.t('ats.candidates.messages.delayed_messages.day_at_time', { day: dayName, time })
  }

  const date = dateMoment.format('ll')
  return i18n.t('ats.candidates.messages.delayed_messages.date_at_time', { date, time })
}

const getRoundedTimeToNextHalfHour = (): { date: Date; time: string } => {
  const now = moment()
  const minutes = now.minutes()

  let roundedMoment: moment.Moment
  if (minutes === 0 || minutes === 30) {
    roundedMoment = now.clone().add(30, 'minutes').startOf('minute')
  } else if (minutes < 30) {
    roundedMoment = now.clone().minutes(30).seconds(0).milliseconds(0)
  } else {
    roundedMoment = now.clone().add(1, 'hour').startOf('hour')
  }

  return {
    date: roundedMoment.toDate(),
    time: roundedMoment.format('HH:mm'),
  }
}

export const DelayMessageSelector = ({
  value,
  onChange,
  onCustomSelect,
  inlineCustomPicker = false,
}: Props) => {
  const { fallbackOption, defaultOption, sendMessageOptions, customOption } = useMessageOptions()
  const [showScheduleModal, setShowScheduleModal] = React.useState(false)
  const [showInlinePicker, setShowInlinePicker] = React.useState(false)

  const userTimezone = momentTimezone.tz.guess()
  const timezoneAbbr = React.useMemo(() => {
    return momentTimezone.tz(userTimezone).format('z')
  }, [userTimezone])

  const roundedDefault = React.useMemo(() => getRoundedTimeToNextHalfHour(), [])
  const [selectedDate, setSelectedDate] = React.useState<Date>(roundedDefault.date)
  const [selectedTime, setSelectedTime] = React.useState<string>(roundedDefault.time)
  const [timeTouched, setTimeTouched] = React.useState(false)

  const isCustomValue = isCustomDateTimeValue(value)
  const hasPresetValue = sendMessageOptions.some((option) => option.value === value)

  const [showDelaySelect, setShowDelaySelect] = React.useState(
    hasPresetValue || Boolean(isCustomValue)
  )

  React.useEffect(() => {
    if (isCustomValue && inlineCustomPicker) {
      const dateMoment = stringToDate(value, 'backend_datetime')
      setSelectedDate(dateMoment.toDate())
      setSelectedTime(dateMoment.format('HH:mm'))
      setShowInlinePicker(true)
    }
  }, [])

  const isTimeInPast = React.useMemo(() => {
    const [hours, minutes] = selectedTime.split(':')
    if (!hours || !minutes) return false

    const scheduledMoment = momentTimezone(selectedDate)
      .set({ hours: parseInt(hours, 10), minutes: parseInt(minutes, 10), seconds: 0 })
      .tz(userTimezone)

    return scheduledMoment.isBefore(moment())
  }, [selectedDate, selectedTime, userTimezone])

  const showTimeError = timeTouched && isTimeInPast

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowDelaySelect(!showDelaySelect)
    if (!event.target.checked) {
      onChange(fallbackOption.value)
      setShowInlinePicker(false)
    }
  }

  const updateScheduledValue = (date: Date, time: string) => {
    const [hours, minutes] = time.split(':')
    if (!hours || !minutes) return

    const scheduledMoment = momentTimezone(date)
      .set({ hours: parseInt(hours, 10), minutes: parseInt(minutes, 10), seconds: 0 })
      .tz(userTimezone)

    if (!scheduledMoment.isBefore(moment())) {
      onChange(scheduledMoment.toISOString())
    }
  }

  const handleDateChange = (
    date: DatePickerValue | undefined,
    _stringValue?: string | undefined
  ) => {
    if (date?.value?.from) {
      setSelectedDate(date.value.from)
      updateScheduledValue(date.value.from, selectedTime)
    }
  }

  const handleTimeChange = (time: string) => {
    setSelectedTime(time)
    updateScheduledValue(selectedDate, time)
  }

  const handleTimeBlur = () => {
    setTimeTouched(true)
  }

  const handleSelectChange = (selectedValue: string) => {
    if (selectedValue === CUSTOM_DELAY_VALUE) {
      if (inlineCustomPicker) {
        setShowInlinePicker(true)
        const rounded = getRoundedTimeToNextHalfHour()
        setSelectedDate(rounded.date)
        setSelectedTime(rounded.time)
        setTimeTouched(false)
        updateScheduledValue(rounded.date, rounded.time)
      } else if (onCustomSelect) {
        onCustomSelect()
      } else {
        setShowScheduleModal(true)
      }
    } else {
      setShowInlinePicker(false)
      onChange(selectedValue)
    }
  }

  const handleCustomScheduleConfirm = (scheduledDate: string) => {
    onChange(scheduledDate)
  }

  const allOptions = [
    ...sendMessageOptions.map((option) => ({
      label: option.label,
      value: option.value,
    })),
    customOption,
  ]

  const selectValue = isCustomValue ? CUSTOM_DELAY_VALUE : value

  const formattedCustomDate =
    isCustomValue && !showInlinePicker ? formatCustomDateTime(value) : null

  const datePickerValue = React.useMemo(
    () => ({
      granularity: 'day' as const,
      value: {
        from: selectedDate,
        to: selectedDate,
      },
    }),
    [selectedDate]
  )

  return (
    <Box gap="s12">
      <Checkbox
        checked={showDelaySelect}
        label={i18n.t('ats.smart_actions.sidepanel.delay')}
        size="large"
        onChange={handleOnChange}
      />

      {showDelaySelect && (
        <Box gap="s8">
          <SelectField
            defaultOption={defaultOption}
            options={allOptions}
            onChange={handleSelectChange}
            value={selectValue}
          />
          {formattedCustomDate && (
            <Text.Normal size="100" color="grey600">
              {i18n.t('ats.candidates.messages.delayed_messages.scheduled_for', {
                date: formattedCustomDate,
              })}
            </Text.Normal>
          )}
          {showInlinePicker && (
            <Box gap="s8">
              <Box gap="s16" flexDirection="row" width="full">
                <Box style={{ flex: '1' }}>
                  <F0DatePicker
                    label={i18n.t('ats.candidates.messages.schedule_message_modal.date')}
                    onChange={handleDateChange}
                    size="sm"
                    value={datePickerValue}
                    minDate={new Date()}
                  />
                </Box>

                <Box flexDirection="row" alignItems="flexEnd" gap="s8" style={{ flex: '1' }}>
                  <Box style={{ flex: '1' }} onBlur={handleTimeBlur}>
                    <Input
                      label={i18n.t('ats.candidates.messages.schedule_message_modal.time')}
                      type="time"
                      value={selectedTime}
                      onChange={handleTimeChange}
                      error={showTimeError}
                    />
                  </Box>
                  <Box paddingBottom="s8" style={{ minWidth: '50px' }}>
                    <Text.Normal size="200" color="grey600" weight="medium">
                      {timezoneAbbr}
                    </Text.Normal>
                  </Box>
                </Box>
              </Box>
              {showTimeError && (
                <Text.Normal size="100" color="radicalRed1000">
                  {i18n.t('ats.candidates.messages.schedule_message_modal.time_in_past_error')}
                </Text.Normal>
              )}
            </Box>
          )}
        </Box>
      )}

      {!onCustomSelect && !inlineCustomPicker && (
        <ScheduleMessageModal
          isOpen={showScheduleModal}
          onClose={() => setShowScheduleModal(false)}
          onConfirm={handleCustomScheduleConfirm}
        />
      )}
    </Box>
  )
}
